/**
 * Responsive image component with lazy loading
 * Provides automatic srcset generation, lazy loading, and responsive breakpoints
 */

import { globalState } from '@/components/helpers/utils'
import { ComponentProps } from 'preact'
import { useEffect, useState } from 'preact/hooks'

interface PreactImageProps extends ComponentProps<'image'> {
  imageUrl: string[] | string
  alt: string
  className: string
  imageClass: string
  isLazy: boolean
  isCover: boolean
  imageSrcSet: string[]
  imageBreakpoints: string;
  imageSizes: number[]
  /** Candidate widths for the generated srcset. */
  widths: number[]
  /** Real `sizes` value describing the rendered box, e.g. '120px'. */
  sizesAttr: string
}

/**
 * Shopify CDN URLs are served at their original resolution unless a `width`
 * parameter is supplied. This component previously handed the raw URL straight to
 * `data-src`, so every rendering downloaded the full master: measured at 1875x2500
 * (~17.9MB decoded) for a mini-cart thumbnail rendered in a 120px box.
 */
function withWidth(url: string, width: number): string {
  if (!url) return url
  const clean = url.replace(/([?&])width=\d+&?/g, '$1').replace(/[?&]$/, '')
  return `${clean}${clean.includes('?') ? '&' : '?'}width=${width}`
}

// Sized for the largest box this component is used in (a 120px cart thumbnail at
// 3x). Pass `widths`/`sizesAttr` if it is ever reused somewhere larger.
const DEFAULT_WIDTHS = [120, 180, 240, 360, 480]
const DEFAULT_SIZES = '120px'

function PreactImage({
  imageUrl = globalState.shopify.defaultImage,
  alt,
  className,
  imageClass,
  isLazy = true,
  isCover = true,
  imageSrcSet = [],
  imageBreakpoints = '(min-width: 0px)',
  imageSizes = [],
  widths = DEFAULT_WIDTHS,
  sizesAttr = DEFAULT_SIZES
}: PreactImageProps) {
  const [currentImageUrls] = useState(imageUrl)
  const classImage = `${isCover ? 'object-cover' : 'object-contain'} ${isLazy && 'lazy'} ${imageClass}`
  const customClass = `relative ${className}`
  let fallbackImageUrl = currentImageUrls[0]
  let breakpoints = imageBreakpoints.includes('|') ? imageBreakpoints.split('|') : [imageBreakpoints]

  if (currentImageUrls.length <= 1) {
    breakpoints = breakpoints.splice(0, 1)
  } else {
    breakpoints = breakpoints.splice(0, currentImageUrls.length)
    fallbackImageUrl = currentImageUrls[currentImageUrls.length - 1]
  }

  // `imageSizes` previously defaulted to [150], emitting `sizes: 150vw` — one and a
  // half viewports for a thumbnail. It is now opt-in, and `sizesAttr` carries a real
  // measurement of the box by default.
  const sizes = imageSizes.length
    ? imageSizes.map((curr, index) => `${breakpoints[index]} ${curr}vw`).join(',')
    : sizesAttr

  useEffect(() => {
    if (window.lazyLoadInstance) {
      window.lazyLoadInstance.update()
    }
  }, [])

  // Every candidate now carries an explicit `width`, so the browser can pick one
  // scaled to the box instead of falling back to the untransformed master.
  const ladder = imageSrcSet.length > 0 ? imageSrcSet.map(Number) : widths
  const getSrcSet = (url: string) =>
    ladder.map((w) => `${withWidth(url, w)} ${w}w`).join(', ')

  const largest = ladder[ladder.length - 1]

  return (
    <picture className={customClass}>
      {breakpoints.map((breakpoint, index) => (
        // `data-sizes` (not `data-size`) is the attribute vanilla-lazyload reads.
        <source className="!size-0" media={breakpoint} data-srcset={getSrcSet(currentImageUrls[index])} data-sizes={sizes} />
      ))}
      <img
        src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAAAXNSR0IArs4c6QAAAA1JREFUCB1j+Pjx438ACX0D04PrXz0AAAAASUVORK5CYII="
        data-src={withWidth(fallbackImageUrl, largest)}
        data-srcset={getSrcSet(fallbackImageUrl)}
        data-sizes={sizes}
        className={classImage}
        alt={alt || 'Image'}
      />
    </picture>
  )
}

export default PreactImage
